import { supabase } from '../lib/supabase';
import axios from 'axios';

const DEBUG = false; // Set this to true only when debugging is needed

export interface SingleChapterVerse {
  id: string;
  book: string;
  verse_number: number;
  text: string;
  version: string;
}

class SingleChapterService {
  // Helper function to normalize book names consistently
  private normalizeBookName(book: string): string {
    // Convert to lowercase and replace spaces with dashes
    const normalized = book.toLowerCase()
      .replace(/\s+/g, '-')  // Replace spaces with dashes
      .replace(/(\d)\s*([a-z])/i, '$1-$2');  // Ensure numbers are properly connected (e.g., "2 John" -> "2-john")
    
    if (DEBUG) {
      console.log('Book name normalization:', {
        original: book,
        normalized
      });
    }
    return normalized;
  }

  async getVerses(book: string, version: string): Promise<SingleChapterVerse[]> {
    if (DEBUG) console.log('=== SingleChapterService.getVerses START ===');

    const normalizedBook = this.normalizeBookName(book);
    const normalizedVersion = version.toLowerCase();
    
    try {
      // First, get verse 1 from the Bible API
      const bibleApiUrl = 'https://bible-api.com';
      const formattedApiBook = normalizedBook.replace(/-/g, '+');
      const verse1Url = `${bibleApiUrl}/${formattedApiBook}+1:1`;
      
      const verse1Response = await axios.get(verse1Url, {
        params: {
          translation: normalizedVersion
        }
      });

      if (!verse1Response.data?.verses?.[0]) {
        throw new Error('Could not fetch verse 1');
      }

      const verse1: SingleChapterVerse = {
        id: `${normalizedBook}-1-${normalizedVersion}`,
        book: normalizedBook,
        verse_number: 1,
        text: verse1Response.data.verses[0].text.trim(),
        version: normalizedVersion
      };

      // First, verify we can access the table
      const { data: tableInfo, error: tableError } = await supabase
        .from('single_chapter_verses')
        .select('count');

      if (tableError) {
        throw tableError;
      }

      // Now get the verses for this specific book and version
      const { data: storedVerses, error } = await supabase
        .from('single_chapter_verses')
        .select('*')
        .eq('book', normalizedBook)
        .eq('version', normalizedVersion)
        .order('verse_number', { ascending: true });
      
      if (error) {
        throw error;
      }

      // Combine verse 1 with stored verses
      const allVerses = [verse1, ...(storedVerses || [])];

      // Sort by verse number to ensure correct order
      allVerses.sort((a, b) => a.verse_number - b.verse_number);

      return allVerses;
    } catch (error) {
      console.error('Error in SingleChapterService.getVerses:', error);
      throw error;
    }
  }

  async addVerse(verse: Omit<SingleChapterVerse, 'id'>) {
    const { data, error } = await supabase
      .from('single_chapter_verses')
      .insert([verse])
      .select();
      
    if (error) throw error;
    return data[0];
  }

  async testConnection() {
    if (DEBUG) console.log('Testing Supabase connection...');
    const { data, error } = await supabase
      .from('single_chapter_verses')
      .select('book, version')
      .limit(1);
    
    if (error) {
      console.error('Supabase connection error:', error);
      return false;
    }
    if (DEBUG) console.log('Supabase connection successful, sample data:', data);
    return true;
  }
}

export const singleChapterService = new SingleChapterService();
